import { AnimatedValue, InterpolatedValue, smoothen } from './animated/AnimatedValue'
import { ScrollVideo, ScrollVideoOptions, ScrollVideoOptionsFull } from './types'
import fit from './utils/fit'
import getPosition from './utils/getPosition'
import setStyle, { Style } from './utils/setStyle'
import videoCache from './utils/videoCache'
import findClosedDefinedIndex from './utils/findClosedDefinedIndex'

export const defaultScrollVideoOptions: ScrollVideoOptionsFull = {
  src: '',
  frames: 0,
  webp: false,
  playbackSpeed: 10,
  focusPoint: { x: 0.5, y: 0.5 },
  fit: 'cover',
  contents: [],
  autoplay: false,
  autoplayDoneAt: false
}

const scrollVideos = new WeakMap<HTMLElement, ScrollVideo>()

export const createOrUpdateScrollVideo = (wrap: HTMLElement, options: ScrollVideoOptions): ScrollVideo => {
  if (scrollVideos.has(wrap)) {
    const scrollVideo = scrollVideos.get(wrap)!
    scrollVideo.update(options)
    return scrollVideo
  }

  const mounted = new AnimatedValue<boolean>(true)
  const onUnmount = (handler: () => unknown) => {
    const listener = (nextMounted: boolean) => {
      if (!nextMounted) {
        handler()
        mounted.removeListener(listener)
      }
    }
    mounted.addListener(listener, false)
  }
  const args = new AnimatedValue<ScrollVideoOptionsFull>({ ...defaultScrollVideoOptions, ...options })

  // Create innerWrap so that the inner Wrap can be position fixed without changing the sites layout
  const innerWrap = document.createElement('div')
  for (const child of Array.from(wrap.children)) {
    wrap.removeChild(child)
    innerWrap.appendChild(child)
  }
  wrap.appendChild(innerWrap)
  onUnmount(() => {
    for (const child of Array.from(innerWrap.children)) {
      innerWrap.removeChild(child)
      wrap.appendChild(child)
    }
    wrap.removeChild(innerWrap)
  })

  // Create a Canvas to draw the video on
  const canvas = document.createElement('canvas')
  innerWrap.prepend(canvas)
  const ctx = canvas.getContext('2d')!
  onUnmount(() => {
    canvas.parentElement && canvas.parentElement.removeChild(canvas)
  })

  const position = new AnimatedValue(getPosition(wrap))
  const scrollPosition = new AnimatedValue(window.pageYOffset)
  const smoothScrollPosition = smoothen(scrollPosition)
  const windowHeight = new AnimatedValue(window.innerHeight)
  const isVisible = new AnimatedValue(!!wrap.offsetParent)
  const onScroll = () => {
    scrollPosition.value = window.pageYOffset
    position.value = getPosition(wrap)
    windowHeight.value = window.innerHeight
    // Wenn display:none bei wrap oder einem parent gesetzt ist, ist offsetParent === null
    isVisible.value = !!wrap.offsetParent
  }
  window.addEventListener('resize', onScroll)
  window.addEventListener('scroll', onScroll, { passive: false })
  onScroll()

  const isHidden = isVisible.interpolate((x) => !x)
  const video = videoCache(args, isHidden)

  const frameSize = new AnimatedValue({ width: 0, height: 0 }).addListener(({ width, height }) => {
    canvas.width = width
    canvas.height = height
    setStyle(canvas, {
      width: width + 'px',
      height: height + 'px'
    })
  })

  const currentScrollFrame = new InterpolatedValue(
    {
      args,
      position,
      scrollPosition,
      smoothScrollPosition
    },
    ({ args: { frames, playbackSpeed }, position, scrollPosition, smoothScrollPosition }) => {
      return Math.floor(
        Math.max(0, Math.min(frames - 1, -(position.top + scrollPosition - smoothScrollPosition) / playbackSpeed))
      )
    }
  )
  const currentAutoPlayFrame = new AnimatedValue(1)
  video.ready.addListener((ready) => {
    currentAutoPlayFrame.value = ready ? args.value.frames : 1
  })
  const autoplayDone = new InterpolatedValue(
    {
      isAutoplay: args.interpolate((x) => x.autoplay),
      currentAutoPlayFrame,
      lastFrame: args.interpolate((x) => (x.autoplayDoneAt !== false ? x.autoplayDoneAt : x.frames) - 1)
    },
    ({ isAutoplay, currentAutoPlayFrame, lastFrame }) => {
      return isAutoplay && !!lastFrame && currentAutoPlayFrame >= lastFrame
    }
  )
  autoplayDone.addListener((done) => {
    if (done) {
      wrap.classList.add('scroll-video--autoplay-done')
    } else {
      wrap.classList.remove('scroll-video--autoplay-done')
    }
  })
  const currentFrame = new InterpolatedValue(
    { currentScrollFrame, currentAutoPlayFrame, args, video: video.cache },
    ({ currentScrollFrame, currentAutoPlayFrame, args, video }) => {
      if (args.autoplay) {
        return currentAutoPlayFrame
      } else {
        return findClosedDefinedIndex(video, currentScrollFrame)
      }
    }
  )
  new InterpolatedValue({ currentFrame, video: video.cache }, ({ currentFrame, video }) => {
    if (video[currentFrame]) {
      return video[currentFrame]
    }
  }).addListener((image) => {
    if (!image) {
      return
    }
    if (canvas.width !== image.naturalWidth || canvas.height !== image.naturalHeight) {
      frameSize.value = {
        width: image.naturalWidth,
        height: image.naturalHeight
      }
    }
    ctx.drawImage(image, 0, 0, image.naturalWidth, image.naturalHeight)
  }, true)

  new InterpolatedValue({ args, isHidden }, ({ args, isHidden }) => {
    if (isHidden) {
      return {
        height: '0px',
        paddingBottom: '0px',
        visibility: 'hidden',
        opacity: '0',
        pointerEvents: 'none'
      }
    } else {
      return {
        //height: '100vh',
        height: 'var(var(--app-height))',
        paddingBottom: args.autoplay ? '0px' : args.playbackSpeed * args.frames + 'px',
        visibility: 'visible',
        opacity: '1',
        pointerEvents: 'all'
      }
    }
  }).addListener((style) => {
    setStyle(wrap, style)
  })

  new InterpolatedValue(
    {
      position,
      windowHeight,
      frameSize,
      focusPoint: args.interpolate((x) => x.focusPoint),
      fitMode: args.interpolate((x) => x.fit)
    },
    ({ position, windowHeight, frameSize, focusPoint, fitMode }): Partial<Style> => {
      const { offset, size } = fit(fitMode, { width: position.width, height: windowHeight }, frameSize, focusPoint)
      return {
        marginLeft: offset.left + 'px',
        marginTop: offset.top + 'px',
        width: size.width + 'px',
        height: size.height + 'px'
      }
    }
  ).addListener((style) => setStyle(canvas, style), true)

  new InterpolatedValue(
    { position, args, isHidden },
    ({ position, args: { frames, playbackSpeed, autoplay }, isHidden }) => {
      let className = 'inner-wrap'
      if (isHidden || position.top > 0 || autoplay || -position.top < playbackSpeed * frames) {
        className += ' fixed'}
      else {
        className += ' bottom'
      }
      return className
    }
  ).addListener((className) => innerWrap.setAttribute('class', className), true)

  const contents = args.interpolate((x) => x.contents)
  const contentElements = contents.interpolate((x) => x.map((y) => y.element))
  const contentOpacities = new InterpolatedValue({ contents, currentFrame }, ({ contents, currentFrame }) => {
    return contents.map(({ showAt, hideAt, fadeInDuration, fadeOutDuration }) => {
      if (currentFrame < showAt || currentFrame > hideAt) {
        return 0
      }
      const fadeIn = Math.min(1, Math.max(0, (currentFrame - showAt) / fadeInDuration))
      const fadeOut = Math.min(1, Math.max(0, (hideAt - currentFrame) / fadeOutDuration))
      return fadeIn * fadeOut
    })
  })
  new InterpolatedValue({ contentOpacities, contentElements }, ({ contentOpacities, contentElements }) =>
    contentElements.map((element, i) => ({
      element,
      opacity: contentOpacities[i]
    }))
  ).addListener((items) => {
    items.forEach(({ element, opacity }) => {
      setStyle(element, {
        opacity: opacity.toString(),
        visibility: opacity === 0 ? 'hidden' : 'visible'
      })
    })
  })

  if (location.hash.length > 1) {
    const anchor = wrap.querySelector(window.location.hash)?.closest('.scroll-video__contents')
    const anchorContent = args.value.contents.find((x) => !!anchor && x.element === anchor)
    if (anchorContent) {
      const scrollPos =
        scrollPosition.value +
        position.value.top +
        Math.max(0, anchorContent.showAt + anchorContent.fadeInDuration) * args.value.playbackSpeed
      window.scrollTo({
        top: scrollPos
      })
    }
  }

  const scrollVideo: ScrollVideo = {
    update: (nextOptions: ScrollVideoOptions) => {
      args.value = { ...args.value, ...nextOptions }
    },
    destroy: () => {
      mounted.value = false
      scrollVideos.delete(wrap)
    }
  }
  scrollVideos.set(wrap, scrollVideo)
  return scrollVideo
}

export const destroyScrollVideo = (wrap: HTMLElement): void => {
  const video = scrollVideos.get(wrap)
  if (video) {
    video.destroy()
  }
}

window.addEventListener('scroll', () => {
  const scrollVideo = document.querySelector<HTMLElement>('canvas');
  const newsticker = document.getElementById('newsticker');

  if (scrollVideo && newsticker) {
      const scrollVideoRect = scrollVideo.getBoundingClientRect();
      const scrollY = window.scrollY;

      // Set the top position of the newsticker to match the Y position of the scroll-video element
      newsticker.style.position = 'absolute';
      newsticker.style.top = `${scrollY + scrollVideoRect.top}px`;
  }
});
